/**
 * Route: `/off-page-briefs/:offPageBriefId` — variant editor workspace
 * (spec §5.7.3).
 *
 * The route is intentionally thin: it owns polling, feature-flag gating,
 * top-of-page status card, and the source-brief keyword resolution (so
 * the header eyebrow reads "keyword" instead of a raw UUID). Everything
 * else lives in `OffPageBriefWorkspace`.
 *
 * Polling: `useOffPageBrief` doesn't refetch on its own; while status is
 * a running FSM value (queued/planning/critiquing/grounding) we drive
 * refetches from here via `refetch()` on a 5-second interval so the
 * status card + variant list stay live.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Page, PageHeader } from '@/components/ui/page';
import { EmptyState } from '@/components/ui/empty';
import { Button } from '@/components/ui/button';
import { DetailError } from '@/components/detail-page';
import { useFeatureFlag } from '@/lib/feature-flags';
import { ApiError, summarizeApiError } from '@/lib/api';
import { useOffPageBrief } from '@/features/off-page-briefs/use-off-page-brief';
import { useRegenerateOffPageBrief } from '@/features/off-page-briefs/use-regenerate-off-page-brief';
import { useCancelOffPageBrief } from '@/features/off-page-briefs/use-cancel-off-page-brief';
import { useBrief } from '@/features/briefs/use-brief';
import {
  OffPageBriefFailed,
  OffPageBriefInProgress,
  OffPageBriefWorkspace,
  OffPageBriefWorkspaceSkeleton,
} from '@/features/off-page-briefs/components/workspace';
import { PLATFORM_LABELS } from '@/features/off-page-briefs/lib/variants';
import type { OffPageBriefPlatform } from '@seo/types';

// Statuses where the workflow is still running — we poll while in these.
const RUNNING_STATUSES = new Set(['queued', 'planning', 'critiquing', 'grounding']);

/** Human label for the regen-complete toast. Falls back to the raw platform
 *  id if the enum ever gets an entry the frontend doesn't know about yet. */
function friendlyPlatformLabel(platform: string): string {
  return PLATFORM_LABELS[platform as OffPageBriefPlatform] ?? platform;
}

export function OffPageBriefDetailRoute(): JSX.Element {
  const enabled = useFeatureFlag('a10_off_page_brief');
  const { offPageBriefId } = useParams({ strict: false }) as {
    offPageBriefId: string;
  };
  const detail = useOffPageBrief(offPageBriefId);
  // Pull the source brief so the header eyebrow reads its keyword. Loading
  // this in parallel avoids blocking the workspace on a second round-trip.
  const sourceBrief = useBrief(detail.data?.brief_id);
  const regenerate = useRegenerateOffPageBrief(offPageBriefId);
  const cancel = useCancelOffPageBrief(offPageBriefId);

  const status = detail.data?.status;
  const isRunning = !!status && RUNNING_STATUSES.has(status);

  // Per-variant regen is initiated from the workspace by clicking "Regen this
  // variant" on a specific platform. We track which platforms are mid-refresh
  // so the route can (a) NOT swap in the full "Writing your variants" progress
  // card — that felt like the whole page froze on a per-variant action — and
  // (b) render the workspace normally with just the affected variant showing
  // a spinner.
  //
  // A ref-backed content_version watcher clears the set once the OPB returns
  // to `ready` with a higher content_version, so the spinner naturally
  // resolves once the new variant is persisted.
  const [partialRegenPlatforms, setPartialRegenPlatforms] = useState<
    Set<string>
  >(new Set());
  // Per-platform startedAt marker. We compare each platform's variant
  // `updated_at` against this marker to know exactly which platforms have
  // finished — content_version alone is too coarse when the tenant kicks
  // off multiple partial regens concurrently (the first completion would
  // otherwise clear every platform's spinner at once).
  const startedAtRef = useRef<Record<string, string>>({});

  // Detect completed platforms on every detail refetch: for each pending
  // platform, if the current variants for that platform have `updated_at`
  // newer than the moment we kicked off its regen, mark it complete + toast.
  useEffect(() => {
    if (partialRegenPlatforms.size === 0) return;
    const completed: string[] = [];
    for (const platform of partialRegenPlatforms) {
      const startedAt = startedAtRef.current[platform];
      if (!startedAt) continue;
      const platformVariants = detail.data?.variants.filter(
        (v) => v.platform === platform,
      );
      if (!platformVariants || platformVariants.length === 0) continue;
      const anyNewer = platformVariants.some(
        (v) => v.updated_at > startedAt,
      );
      if (anyNewer) completed.push(platform);
    }
    if (completed.length === 0) return;
    setPartialRegenPlatforms((prev) => {
      const next = new Set(prev);
      for (const p of completed) next.delete(p);
      return next;
    });
    for (const platform of completed) {
      delete startedAtRef.current[platform];
      toast.success(`${friendlyPlatformLabel(platform)} regenerated`, {
        description: 'The updated variant is ready.',
      });
    }
  }, [detail.data?.variants, partialRegenPlatforms]);

  const partialRegenActive = partialRegenPlatforms.size > 0;

  const startPartialRegen = (platform: string): void => {
    setPartialRegenPlatforms((prev) => {
      const next = new Set(prev);
      next.add(platform);
      return next;
    });
    startedAtRef.current[platform] = new Date().toISOString();
  };

  const cancelPartialRegen = (): void => {
    // Cancels the whole workflow (Temporal cancels are OPB-scoped) — since
    // partial regens' workflows only touch scoped variants, untouched
    // variants stay put. On the backend, compensation now honors the partial
    // flag and leaves the OPB row at `ready` instead of flipping to failed.
    // Locally we drop the tracked pending set + startedAt markers so the
    // spinners clear right away instead of waiting for the poll.
    cancel.mutate(undefined, {
      onSuccess: () => {
        toast.success('Cancelling regenerate', {
          description: 'Your existing variants will stay as they are.',
        });
        startedAtRef.current = {};
        setPartialRegenPlatforms(new Set());
      },
      onError: (e) => {
        const { title, description } = summarizeApiError(e, {
          fallbackTitle: 'Could not cancel',
        });
        toast.error(title, { description });
      },
    });
  };

  // Simple 5s poll while the workflow is running. React Query's built-in
  // `refetchInterval` would work too but keeping it here keeps the hook
  // signature stable for callers that don't want polling (list, etc).
  useEffect(() => {
    if (!isRunning) return;
    const id = window.setInterval(() => {
      void detail.refetch();
    }, 5000);
    return () => window.clearInterval(id);
  }, [isRunning, detail]);

  if (!enabled) {
    return (
      <Page>
        <EmptyState
          icon={Megaphone}
          title="This feature is not available"
          description="Off-Page Brief is not enabled for your workspace."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/briefs">Go to Briefs</Link>
            </Button>
          }
        />
      </Page>
    );
  }

  if (detail.isLoading) {
    return (
      <Page>
        <PageHeader title="Off-Page Brief" eyebrow="Loading…" />
        <OffPageBriefWorkspaceSkeleton />
      </Page>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <Page>
        <DetailError
          error={detail.error}
          entityKind="off-page brief"
          backTo="/off-page-briefs"
          backLabel="Off-Page Briefs"
        />
      </Page>
    );
  }

  const d = detail.data;
  const keyword = sourceBrief.data?.target_keyword ?? null;

  const retry = (): void => {
    regenerate.mutate(
      {},
      {
        onSuccess: () => {
          toast.success('Retrying', {
            description: 'Re-running the off-page brief workflow.',
          });
        },
        onError: (e) => {
          if (
            e instanceof ApiError &&
            e.code === 'OFF_PAGE_BRIEF_ALREADY_REGENERATING'
          ) {
            toast.info('Already retrying', {
              description: 'A retry is already in flight — hang tight.',
            });
            return;
          }
          const { title, description } = summarizeApiError(e, {
            fallbackTitle: 'Could not retry',
          });
          toast.error(title, { description });
        },
      },
    );
  };

  const stopRun = (): void => {
    cancel.mutate(undefined, {
      onSuccess: () => {
        toast.success('Cancelling', {
          description: 'Stopping the run — the status will update in a moment.',
        });
      },
      onError: (e) => {
        const { title, description } = summarizeApiError(e, {
          fallbackTitle: 'Could not cancel',
        });
        toast.error(title, { description });
      },
    });
  };

  return (
    <Page>
      <PageHeader
        title={keyword ? `Off-Page Brief — ${keyword}` : 'Off-Page Brief'}
        eyebrow="Off-Page Brief"
        description={
          // The source brief is fetched anyway (for the keyword) — surface it
          // as a real link instead of a text-only reference.
          <Link
            to="/briefs/$briefId"
            params={{ briefId: d.brief_id }}
            className="font-medium underline underline-offset-2 hover:text-primary"
          >
            View source brief
          </Link>
        }
      />

      {isRunning && !partialRegenActive ? (
        <>
          <OffPageBriefInProgress
            status={status as 'queued' | 'planning' | 'critiquing' | 'grounding'}
            updatedAt={d.updated_at}
            onCancel={stopRun}
            cancelling={cancel.isPending}
          />
          {/* Show the workspace read-only underneath so any existing variants
              from a previous version stay visible while the fresh run
              completes. If no variants exist yet, this collapses to nothing. */}
          {d.variants.length > 0 ? (
            <div className="pointer-events-none opacity-60">
              <OffPageBriefWorkspace detail={d} />
            </div>
          ) : null}
        </>
      ) : status === 'failed' || status === 'cancelled' ? (
        <>
          <OffPageBriefFailed
            detail={d}
            onRetry={retry}
            retrying={regenerate.isPending}
          />
          {d.variants.length > 0 ? (
            <OffPageBriefWorkspace
              detail={d}
              partialRegenPlatforms={partialRegenPlatforms}
              onStartPartialRegen={startPartialRegen}
              onCancelPartialRegen={cancelPartialRegen}
            />
          ) : null}
        </>
      ) : (
        <OffPageBriefWorkspace
          detail={d}
          partialRegenPlatforms={partialRegenPlatforms}
          onStartPartialRegen={startPartialRegen}
          onCancelPartialRegen={cancelPartialRegen}
        />
      )}
    </Page>
  );
}
